import * as React from "react";
import { tokens, makeStyles } from "@fluentui/react-components";
import { ArrowUpload16Regular, Dismiss16Regular, Search16Regular } from "@fluentui/react-icons";
import FileList from "./FileList";
import UploadModel from "./UploadModel";

export interface Props {
  icon: React.JSX.Element;
  path: string;
  primaryText: string;
  level?: number;
  link?: string;
  isDirectory: boolean;
  isSelected?: boolean;
  canUpload?: boolean;
  canSearch?: boolean;
  onClick?: () => void;
}

const useStyles = makeStyles({
  listItem: {
    padding: "10px",
    display: "flex",
    alignItems: "center",
    gap: "5px",
    "&:hover": {
      backgroundColor: tokens.colorNeutralBackground4,
      cursor: "pointer",
    },
  },
  listItemSelected: {
    padding: "10px",
    display: "flex",
    alignItems: "center",
    backgroundColor: tokens.colorNeutralBackground5,
    gap: "5px",
    "&:hover": {
      cursor: "pointer",
    },
  },
  icon: {
    marginRight: "10px",
  },
  itemText: {
    fontSize: tokens.fontSizeBase300,
    fontColor: tokens.colorNeutralBackgroundStatic,
    wordWrap: "break-word",
    overflow: "hidden",
  },
  listButton: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: "100%",
    height: "24px",
    width: "24px",
    display: "grid",
    placeItems: "center",
  },
});

const FileListItem: React.FC<Props> = ({
  icon,
  primaryText,
  path,
  level,
  link,
  isDirectory,
  isSelected,
  canUpload,
  canSearch,
  onClick,
}: Props) => {
  const [isSearching, setIsSearching] = React.useState<boolean>(false);
  const [searchQuery, setSearchQuery] = React.useState<string>("");
  const [showUpload, setShowUpload] = React.useState<boolean>(false);
  const [fileListKey, setFileListKey] = React.useState<number>(0);

  const styles = useStyles();

  function toggleSearch(e) {
    e.stopPropagation();

    setSearchQuery("");
    setIsSearching(!isSearching);
  }

  function onSearchQueryChange(e) {
    setSearchQuery(e.target.value);
  }

  async function openUpload(e) {
    e.stopPropagation();
    setShowUpload(true);
  }

  function closeUpload() {
    setShowUpload(false);
    setFileListKey(fileListKey + 1);
  }

  function getType() {
    switch (path) {
      case "/customers":
        return "client_number";
      case "/claims":
        return "claim_number";
      case "/quotes":
        return "quote_reference";
      case "/recoveries":
        return "recovery_id";
      case "/brokers-agents":
        return "broker_code";
      case "/salvage":
        return "salvage_id";
      case "/policies":
        return "policy_cover_number";
      default:
        return "";
    }
  }

  return (
    <>
      {showUpload && <UploadModel type={getType()} destination={primaryText} onClose={closeUpload} />}
      <li className={isSelected ? styles.listItemSelected : styles.listItem} onClick={onClick}>
        <i className={styles.icon}>{icon}</i>
        {isSearching && <input type="text" value={searchQuery} onChange={onSearchQueryChange} autoFocus={true} />}
        {!isSearching && <span className={styles.itemText}>{primaryText}</span>}
        <div style={{ flex: "1" }} />
        {canSearch && isDirectory && (
          <div className={styles.listButton} onClick={toggleSearch}>
            {isSearching && <Dismiss16Regular color={"rgba(0,0,0,0.5)"} />}
            {!isSearching && <Search16Regular color={"rgba(0,0,0,0.5)"} />}
          </div>
        )}
        {canUpload && isDirectory && (
          <div className={styles.listButton} onClick={openUpload}>
            <ArrowUpload16Regular color={"rgba(0,0,0,0.5)"} />
          </div>
        )}
      </li>
      {isSelected && <FileList level={level + 1} path={`/${link}`} searchQuery={searchQuery} key={fileListKey} />}
    </>
  );
};

export default FileListItem;
